function radToDeg(rad) {
  return (Number(rad) * 180) / Math.PI;
}

const TEXT_2D_EDITOR_SCALE_Z = 0.000001;

function getTextEditorScaleZ(textMaterial) {
  const is3D = textMaterial?.textType === '3D';
  if (is3D) {
    const extrusion = Number(textMaterial?.extrusion);
    return Number.isFinite(extrusion) && extrusion > 0 ? extrusion : 0.001;
  }
  return TEXT_2D_EDITOR_SCALE_Z;
}

function getTextPanelScale(textMesh) {
  const scale = textMesh?.scale;
  if (!scale) return { x: 1, y: 1, z: 1 };

  // Published GLB text bakes scale into geometry; panel === scene scale.
  if (textMesh?.userData?.scaleBakedIntoGeometry) {
    return {
      x: scale.x ?? 1,
      y: scale.y ?? 1,
      z: scale.z ?? 1,
    };
  }

  const baseZ = getTextEditorScaleZ(textMesh.userData?.text_material);
  return {
    x: scale.x ?? 1,
    y: scale.y ?? 1,
    z: baseZ > 0 ? (scale.z ?? baseZ) / baseZ : scale.z ?? 1,
  };
}

/** Plain Redux record for a live text mesh (all editor properties). */
export function buildTextReduxAssetRecord(textMesh) {
  if (!textMesh || textMesh.deleteType !== 'text') return null;

  const tm = textMesh.userData?.text_material || {};
  const panelScale = getTextPanelScale(textMesh);
  const objectId =
    textMesh.name ||
    textMesh._id ||
    textMesh.url ||
    tm.id;

  return {
    _id: textMesh._id || objectId,
    id: textMesh.id || textMesh._id || objectId,
    name: textMesh.file_name || textMesh.asset_file_name || textMesh.name,
    file_name: textMesh.file_name,
    asset_file_name: textMesh.asset_file_name,
    temp_file_name: textMesh.temp_file_name || textMesh.name,
    url: textMesh.url || objectId,
    deleteType: 'text',
    visible: textMesh.visible,
    position_x: textMesh.position.x,
    position_y: textMesh.position.y,
    position_z: textMesh.position.z,
    rotation_x: radToDeg(textMesh.rotation.x),
    rotation_y: radToDeg(textMesh.rotation.y),
    rotation_z: radToDeg(textMesh.rotation.z),
    scale_x: panelScale.x,
    scale_y: panelScale.y,
    scale_z: panelScale.z,
    textType: tm.textType,
    content: tm.content,
    fontSize: tm.fontSize,
    color: tm.color,
    horizontal: tm.horizontal,
    vertical: tm.vertical,
    extrusion: tm.extrusion,
    bevelSize: tm.bevelSize,
    curveSegments: tm.curveSegments,
    metalness: tm.metalness,
    roughness: tm.roughness,
    fontFamily: tm.fontFamily,
    fontWeight: tm.fontWeight,
    fontPath: tm.fontPath,
    text_material: {
      ...tm,
      ...(textMesh.userData?.pivotEdited || textMesh.pivotEdited
        ? {
            pivot_edited: true,
            ...(Array.isArray(textMesh.userData?.pivotContentMatrix) &&
            textMesh.userData.pivotContentMatrix.length === 16
              ? {
                  pivot_content_matrix: textMesh.userData.pivotContentMatrix,
                }
              : {}),
            ...(textMesh.userData?.pivotOffset
              ? { pivot_offset: textMesh.userData.pivotOffset }
              : {}),
          }
        : {}),
    },
    events: textMesh.events || tm.events || [],
    pivot_edited: !!(
      textMesh.userData?.pivotEdited ||
      textMesh.pivotEdited ||
      tm.pivot_edited
    ),
    ...(Array.isArray(textMesh.userData?.pivotContentMatrix) &&
    textMesh.userData.pivotContentMatrix.length === 16
      ? { pivot_content_matrix: textMesh.userData.pivotContentMatrix }
      : Array.isArray(tm.pivot_content_matrix) &&
          tm.pivot_content_matrix.length === 16
        ? { pivot_content_matrix: tm.pivot_content_matrix }
        : {}),
    ...(textMesh.userData?.pivotOffset || tm.pivot_offset
      ? {
          pivot_offset:
            textMesh.userData?.pivotOffset || tm.pivot_offset,
        }
      : {}),
  };
}
